/**
 * Theme Context for Vendor Portal.
 *
 * The 7-theme switcher was retired in favour of one premium brand identity, so
 * this is now a thin compatibility shim: `useTheme()` keeps its exact shape
 * (~20 screens still call it) but always returns the brand theme, and the
 * setters are no-ops. The CSS-variable effect still runs so any consumer reading
 * `var(--color-*)` / `var(--grad-*)` works, now publishing the premium tokens too.
 */

import React, { createContext, useContext, useEffect, useCallback } from 'react';
import type { Theme, ThemeId } from '../lib/types';
import { getTheme, themes } from '../lib/themes';
import { gradient, shadow } from '../lib/designTokens';

interface ThemeContextType {
  theme: Theme;
  themeId: ThemeId;
  previewThemeId: ThemeId | null;
  setThemeId: (id: ThemeId) => Promise<void>;
  setPreviewTheme: (id: ThemeId | null) => void;
  isUpdating: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Retained for call-site compatibility (index.tsx); ignored — one identity. */
  initialThemeId?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const theme = getTheme();

  // Publish brand colors + premium tokens as CSS variables on <html>.
  useEffect(() => {
    const root = document.documentElement;
    const colors = theme.colors;

    // Primary
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-hover', colors.primaryHover);
    root.style.setProperty('--color-primary-light', colors.primaryLight);
    root.style.setProperty('--color-primary-dark', colors.primaryDark);
    // Secondary
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-secondary-hover', colors.secondaryHover);
    root.style.setProperty('--color-secondary-light', colors.secondaryLight);
    // Accent
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-accent-hover', colors.accentHover);
    root.style.setProperty('--color-accent-light', colors.accentLight);
    // Status
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-success-light', colors.successLight);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-warning-light', colors.warningLight);
    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-error-light', colors.errorLight);
    root.style.setProperty('--color-info', colors.info);
    root.style.setProperty('--color-info-light', colors.infoLight);
    // Sidebar
    root.style.setProperty('--color-sidebar', colors.sidebar);
    root.style.setProperty('--color-sidebar-hover', colors.sidebarHover);
    root.style.setProperty('--color-sidebar-active', colors.sidebarActive);
    root.style.setProperty('--color-sidebar-text', colors.sidebarText);
    // Header
    root.style.setProperty('--color-header-bg', colors.headerBg);
    root.style.setProperty('--color-header-text', colors.headerText);
    // Buttons
    root.style.setProperty('--color-button-bg', colors.buttonBg);
    root.style.setProperty('--color-button-hover', colors.buttonHover);
    // Misc
    root.style.setProperty('--color-card-border', colors.cardBorder);
    root.style.setProperty('--color-progress-bar', colors.progressBar);
    // Table
    root.style.setProperty('--color-table-header', colors.tableHeader);
    root.style.setProperty('--color-table-header-text', colors.tableHeaderText);
    root.style.setProperty('--color-table-row-hover', colors.tableRowHover);

    // Premium tokens (gradients + elevation) for primitives that read CSS vars.
    root.style.setProperty('--grad-brand', gradient.brand);
    root.style.setProperty('--grad-hero', gradient.hero);
    root.style.setProperty('--grad-accent', gradient.accent);
    root.style.setProperty('--shadow-sm', shadow.sm);
    root.style.setProperty('--shadow-md', shadow.md);
    root.style.setProperty('--shadow-lg', shadow.lg);
    root.style.setProperty('--shadow-brand', shadow.brand);
  }, [theme]);

  // Switching is retired; setters are inert but kept so the hook shape is stable.
  const setThemeId = useCallback(async (_id: ThemeId) => {}, []);
  const setPreviewTheme = useCallback((_id: ThemeId | null) => {}, []);

  return (
    <ThemeContext.Provider
      value={{ theme, themeId: 1, previewThemeId: null, setThemeId, setPreviewTheme, isUpdating: false }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export { themes };
